// ═══════════════════════════════════════════════════════════════
// V·IAnet Governance — AdminGlosario (migrado al shell · prompt 0471 M10)
// CRUD de terminos del glosario con categoria, sinonimos y noTraducir
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { C } from '@/lib/design';
import {
  fetchGlosario,
  crearTermino,
  actualizarTermino,
  eliminarTermino,
  type TerminoGlosario,
  type CategoriaTermino,
  type CrearTerminoInput,
} from '../../../../shared/services/api';
import { useI18n } from '../../../../../i18n';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { CATEGORIA_STYLES, CATEGORIAS_ORDER } from '../../../../shared/constants/glosario-styles';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

const CATEGORIAS: CategoriaTermino[] = CATEGORIAS_ORDER;

interface TerminoForm {
  termino: string;
  definicion: string;
  categoria: CategoriaTermino;
  sinonimos: string[];
  noTraducir: boolean;
  idioma: string;
}

const emptyForm: TerminoForm = {
  termino: '',
  definicion: '',
  categoria: 'GENERAL',
  sinonimos: [],
  noTraducir: false,
  idioma: 'es',
};

function EditIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.azul2}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#DC2626"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function PowerIcon({ activo }: { activo: boolean }) {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke={activo ? '#16A34A' : '#9CA3AF'}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2v10" />
      <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />
    </svg>
  );
}

function TerminoModal({
  termino,
  onClose,
  onSaved,
}: {
  termino: TerminoGlosario | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const { t } = useI18n();
  const adm = t.glosario.admin;
  const catLabels = t.glosario.categoria;
  const isEdit = termino !== null;
  const [form, setForm] = useState<TerminoForm>(() =>
    isEdit
      ? {
          termino: termino.termino,
          definicion: termino.definicion,
          categoria: termino.categoria,
          sinonimos: [...(termino.sinonimos ?? [])],
          noTraducir: termino.noTraducir,
          idioma: termino.idioma,
        }
      : { ...emptyForm },
  );
  const [sinonimoInput, setSinonimoInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = <K extends keyof TerminoForm>(k: K, v: TerminoForm[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError('');
  };

  const addSinonimo = () => {
    const v = sinonimoInput.trim();
    if (!v) return;
    if (form.sinonimos.includes(v)) {
      setSinonimoInput('');
      return;
    }
    set('sinonimos', [...form.sinonimos, v]);
    setSinonimoInput('');
  };

  const removeSinonimo = (s: string) => {
    set(
      'sinonimos',
      form.sinonimos.filter((x) => x !== s),
    );
  };

  const onSinonimoKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addSinonimo();
    }
  };

  const handleSave = async () => {
    if (!form.termino.trim() || !form.definicion.trim()) {
      setError(adm.required);
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload: CrearTerminoInput = {
        termino: form.termino.trim(),
        definicion: form.definicion.trim(),
        categoria: form.categoria,
        sinonimos: form.sinonimos,
        noTraducir: form.noTraducir,
        idioma: form.idioma || 'es',
      };
      if (isEdit) {
        await actualizarTermino(termino.id, payload);
        onSaved(adm.updated);
      } else {
        await crearTermino(payload);
        onSaved(adm.created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : adm.errorSave);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: `3px solid ${C.azul1}`,
          width: 560,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#0E243E' }}>
          {isEdit ? adm.editTerm : adm.newTerm}
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lblStyle}>{adm.fieldTermino} *</label>
            <input
              type="text"
              value={form.termino}
              onChange={(e) => set('termino', e.target.value)}
              style={inpStyle}
              maxLength={200}
            />
          </div>

          <div>
            <label style={lblStyle}>{adm.fieldDefinicion} *</label>
            <textarea
              value={form.definicion}
              onChange={(e) => set('definicion', e.target.value)}
              rows={4}
              style={{ ...inpStyle, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>

          <div>
            <label style={lblStyle}>{adm.fieldCategoria}</label>
            <select
              value={form.categoria}
              onChange={(e) => set('categoria', e.target.value as CategoriaTermino)}
              style={selStyle}
            >
              {CATEGORIAS.map((c) => (
                <option key={c} value={c}>
                  {catLabels[c]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={lblStyle}>{adm.fieldSinonimos}</label>
            <div
              style={{
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                padding: '6px 8px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                background: '#fff',
                minHeight: 40,
                alignItems: 'center',
              }}
            >
              {form.sinonimos.map((s) => (
                <span
                  key={s}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 12,
                    background: '#EFF6FF',
                    color: C.azul1,
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {s}
                  <button
                    type="button"
                    onClick={() => removeSinonimo(s)}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: C.azul1,
                      padding: 0,
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={sinonimoInput}
                onChange={(e) => setSinonimoInput(e.target.value)}
                onKeyDown={onSinonimoKey}
                onBlur={addSinonimo}
                placeholder={adm.synonymsHint}
                style={{
                  flex: 1,
                  minWidth: 120,
                  border: 'none',
                  outline: 'none',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  padding: '2px 4px',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={form.noTraducir}
                onChange={(e) => set('noTraducir', e.target.checked)}
              />
              {adm.fieldNoTraducir}
            </label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 13, color: '#374151' }}>{adm.fieldIdioma}:</label>
              <input
                type="text"
                value={form.idioma}
                onChange={(e) => set('idioma', e.target.value)}
                maxLength={5}
                style={{ ...inpStyle, width: 60, padding: '6px 8px' }}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                background: '#FEF2F2',
                color: '#B91C1C',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '9px 18px',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              background: '#fff',
              color: '#374151',
              fontSize: 13,
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {adm.cancel}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '9px 22px',
              background: C.azul2,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {adm.save}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminGlosario() {
  const { t } = useI18n();
  const adm = t.glosario.admin;
  const admStructure = t.structure.admin;
  const catLabels = t.glosario.categoria;
  const [terminos, setTerminos] = useState<TerminoGlosario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState<'' | CategoriaTermino>('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modal, setModal] = useState<'new' | TerminoGlosario | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TerminoGlosario | null>(null);
  const [toast, setToast] = useState('');
  const mountedRef = useRef(true);
  const toastTimerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchGlosario();
      if (!mountedRef.current) return;
      setTerminos(res.data ?? []);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : t.glosario.errorLoading);
      setTerminos([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [t.glosario.errorLoading]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      if (mountedRef.current) setToast('');
      toastTimerRef.current = null;
    }, 3000);
  };

  const handleDeleteRequest = (term: TerminoGlosario) => {
    setConfirmDelete(term);
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    try {
      await eliminarTermino(confirmDelete.id);
      showToast(adm.deleted);
      load();
    } catch {
      showToast(adm.errorDelete);
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleToggleActivo = async (term: TerminoGlosario) => {
    try {
      await actualizarTermino(term.id, {
        termino: term.termino,
        definicion: term.definicion,
        categoria: term.categoria,
        sinonimos: term.sinonimos,
        noTraducir: term.noTraducir,
        idioma: term.idioma,
        activo: !term.activo,
      } as unknown as CrearTerminoInput);
      showToast(adm.updated);
      load();
    } catch {
      showToast(adm.errorSave);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return terminos
      .filter((x) => (mostrarInactivos ? true : x.activo !== false))
      .filter((x) => (categoria ? x.categoria === categoria : true))
      .filter((x) => {
        if (!q) return true;
        if ((x.codigo ?? '').toLowerCase().includes(q)) return true;
        if (x.termino.toLowerCase().includes(q)) return true;
        if (x.definicion.toLowerCase().includes(q)) return true;
        if (x.sinonimos?.some((s) => s.toLowerCase().includes(q))) return true;
        return false;
      })
      .sort((x, y) => x.termino.localeCompare(y.termino));
  }, [terminos, search, categoria, mostrarInactivos]);

  const filters = (
    <select
      value={categoria}
      onChange={(e) => setCategoria(e.target.value as '' | CategoriaTermino)}
      style={{
        padding: '7px 10px',
        border: '1px solid #D1D5DB',
        borderRadius: 6,
        fontSize: 13,
        fontFamily: 'inherit',
        background: '#fff',
        outline: 'none',
      }}
    >
      <option value="">{t.glosario.all}</option>
      {CATEGORIAS.map((c) => (
        <option key={c} value={c}>
          {catLabels[c]}
        </option>
      ))}
    </select>
  );

  const columns: AdminTableColumn<TerminoGlosario>[] = [
    {
      key: 'codigo',
      label: admStructure.colCodigo ?? 'Codigo',
      sortable: true,
      sortKey: (r) => r.codigo ?? '',
      render: (r) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.azul2 }}>
          {r.codigo && r.codigo.length > 0 ? r.codigo : '—'}
        </span>
      ),
    },
    {
      key: 'termino',
      label: adm.fieldTermino,
      sortable: true,
      sortKey: (r) => r.termino,
      render: (r) => <span style={{ fontWeight: 600 }}>{r.termino}</span>,
    },
    {
      key: 'categoria',
      label: adm.fieldCategoria,
      sortable: true,
      sortKey: (r) => catLabels[r.categoria],
      render: (r) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: CATEGORIA_STYLES[r.categoria].bg,
            color: CATEGORIA_STYLES[r.categoria].color,
            whiteSpace: 'nowrap',
          }}
        >
          {catLabels[r.categoria]}
        </span>
      ),
    },
    {
      key: 'definicion',
      label: adm.fieldDefinicion,
      render: (r) => (
        <span
          style={{
            color: '#6B7280',
            display: 'inline-block',
            maxWidth: 380,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={r.definicion}
        >
          {r.definicion.length > 80 ? `${r.definicion.slice(0, 80)}…` : r.definicion}
        </span>
      ),
    },
    {
      key: 'sinonimos',
      label: adm.fieldSinonimos,
      align: 'center',
      width: 90,
      render: (r) => <span>{r.sinonimos?.length ?? 0}</span>,
    },
    {
      key: 'noTraducir',
      label: adm.fieldNoTraducir,
      align: 'center',
      width: 100,
      render: (r) =>
        r.noTraducir ? (
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#059669"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <span style={{ color: '#CBD5E1' }}>—</span>
        ),
    },
    {
      key: 'activo',
      label: (admStructure as unknown as Record<string, string>).activo ?? 'Activo',
      align: 'center',
      width: 90,
      render: (r) => (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: r.activo ? '#ECFDF5' : '#FEF2F2',
            color: r.activo ? '#16A34A' : '#DC2626',
          }}
        >
          {r.activo ? t.common.estados.activo : t.common.estados.inactivo}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={adm.title}
        infoTooltip={{
          title: t.structure.tooltips.glosario.titulo,
          text: t.structure.tooltips.glosario.ayuda,
        }}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t.glosario.searchPlaceholder,
          width: 260,
        }}
        filters={filters}
        onNew={{
          label: adm.newTerm,
          onClick: () => setModal('new'),
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {admStructure.mostrarInactivos}
          </label>
        }
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: '#0E243E',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      {error ? (
        <div
          style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: 16,
            color: '#B91C1C',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      ) : (
        <AdminTableShell
          columns={columns}
          rows={filtered}
          rowKey={(r) => r.id}
          loading={loading}
          loadingMessage={t.glosario.loading}
          emptyMessage={t.glosario.empty}
          actions={(r) => (
            <>
              <AdminActionButton
                icon={<EditIcon />}
                label={adm.editTerm}
                onClick={() => setModal(r)}
              />
              <AdminActionButton
                icon={<PowerIcon activo={r.activo} />}
                label={
                  r.activo
                    ? admStructure.tooltips.toggleDesactivar
                    : admStructure.tooltips.toggleActivar
                }
                onClick={() => handleToggleActivo(r)}
              />
              <AdminActionButton
                icon={<TrashIcon />}
                label={adm.confirmDelete}
                variant="danger"
                onClick={() => handleDeleteRequest(r)}
              />
            </>
          )}
          footer={
            !loading ? (
              <span>
                {filtered.length} de {terminos.length} {admStructure.glosarioContador ?? 'terminos'}
              </span>
            ) : undefined
          }
        />
      )}

      {modal !== null && (
        <TerminoModal
          termino={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={(msg) => {
            setModal(null);
            load();
            showToast(msg);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        title={adm.editTerm}
        message={
          confirmDelete ? `${adm.confirmDelete}\n\n"${confirmDelete.termino}"` : adm.confirmDelete
        }
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

const lblStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#5A6171',
  marginBottom: 4,
  display: 'block',
};

const inpStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  fontSize: 13,
  width: '100%',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const selStyle: React.CSSProperties = {
  ...inpStyle,
  background: '#fff',
  appearance: 'auto',
};
